<template>
  <div class="graph-block">
    <div class="p-contentplans__header">
      <div class="l-block__header">
        <TitleSub level="h3">
          <span class="title">キーワードのインデックス数</span>
          <Tooltip class="l-ml-1">
            「site:〇〇（ドメイン）+調査キーワード」で検索した結果の値です。<br />
            調査キーワードのインデックス数が多いほど、関連したページ数が多いサイトであると判断できます。<br />
            <br />
            ◆中央値とは？<br />
            1位～10位の検索結果を昇順に並べ、中央（5位～6位）の値を足して2で割った値です。<br />
            極端に多い流入数（外れ値）を持ったサイトが含まれる場合にも、その影響をほとんど受けない値の算出が出来ます。
          </Tooltip>
        </TitleSub>
      </div>
      <template v-if="!isLoading && median"
        ><div class="average">
          <span class="min">中央値</span
          ><span class="num">{{
            median | roundFloatingPointNumber(1) | separateNumber
          }}</span>
        </div>
      </template>
    </div>
    <LoadingChara v-if="isLoading" variant="inline" size="sm">
      <template slot="title">
        データ取得中
      </template>
    </LoadingChara>
    <Error variant="inline" v-else-if="isError" size="sm">
      <template slot="title">
        データが取得できませんでした。<br />
        ページを再読み込みしてください。
      </template>
      <Button variant="border" size="w80" fsize="f11" @click="reload">
        <Icon variant="return_gn" :size="12" />
        リロード
      </Button>
    </Error>
    <template v-else>
      <ul class="graph-area graph-area__keywordIndexCount">
        <ListItemKeywordIndexCount
          v-for="(item, index) in data.items"
          :project="project"
          :key="index"
          :item="item"
          :max="max"
        />
      </ul>
    </template>
  </div>
</template>

<script>
import Button from '@/components/Button';
import Error from '@/components/Error';
import Icon from '@/components/Icon';
import ListItemKeywordIndexCount from '@/components/contentPlans/ListItemKeywordIndexCount';
import LoadingChara from '@/components/LoadingChara';
import TitleSub from '@/components/TitleSub';
import Tooltip from '@/components/Tooltip';
import apiProjects from '@/api/projects';
import axios from 'axios';
import utils from '@/utils';

export default {
  props: ['createdAt', 'keyword', 'project', 'serpsTopItems', 'serpsAllItems'],

  components: {
    Button,
    Error,
    Icon,
    ListItemKeywordIndexCount,
    LoadingChara,
    TitleSub,
    Tooltip
  },

  data() {
    return {
      data: {
        items: []
      },
      isLoading: true,
      cnt: 0,
      timeout: null,
      return: [],
      flg: true,
      isError: false
    };
  },

  computed: {
    median() {
      const filteredItems = this.data.items.filter(item => {
        return (
          item.rank &&
          item.rank >= 1 &&
          item.rank <= 10 &&
          (item.headers.index || item.headers.index === 0)
        );
      });
      if (filteredItems.length === 0) {
        return 0;
      }
      const headersIndexes = filteredItems.map(item => {
        return item.headers.index;
      });
      return utils.calcMedian(headersIndexes);
    },
    max() {
      let max = 0;
      let count = this.data.items.length;
      for (let i = 0; i < count; i++) {
        let index = this.data.items[i].headers.index;
        if (index > max) {
          max = index;
        }
      }
      return max;
    }
  },

  watch: {
    serpsTopItems: {
      handler() {
        this.init();
      },
      deep: true,
      immediate: true
    }
  },

  destroyed() {
    clearTimeout(this.timeout);

    // 非同期通信をキャンセル
    this.flg = false;
    for (let i = 0; i < this.return.length; i++) {
      if (this.return[i]) {
        this.return[i].cancel();
      }
    }
  },
  methods: {
    init() {
      if (!this.serpsTopItems.length) {
        return;
      }
      // 配列のコピー
      this.data.items = JSON.parse(JSON.stringify(this.serpsTopItems));
      let siteCheck = false;

      for (let i = 0; i < this.data.items.length; i++) {
        if (this.data.items[i].url.indexOf(this.project.siteUrl) !== -1) {
          siteCheck = true;
          this.data.items[i].active = true;
        }
      }
      if (!siteCheck) {
        this.data.items.push({
          url: this.project.siteUrl,
          active: true
        });
      }

      this.data.items = this.data.items.map((item, index) => {
        item.headers = { index: null };
        return item;
      });

      this.data.items.map((item, index) => {
        // 11位以下のrankの取得
        if (!item.rank) {
          for (let i = 0; i < this.serpsAllItems.length; i++) {
            if (this.serpsAllItems[i].url.indexOf(item.url) !== -1) {
              item.rank = this.serpsAllItems[i].rank;
              break;
            }
          }
        }
        return this.getKeywordIndex(item.url, index, item.rank, item.active);
      });
    },
    getKeywordIndex(url, index, rank, active) {
      let params = {
        dateJst: this.$options.filters.dateText(this.createdAt, 'YYYY-MM-DD'),
        url: url,
        keyword: this.keyword
      };
      this.return[index] = axios.CancelToken.source();
      return apiProjects.contentPlans.siteKeywordIndex
        .get(
          params,
          this.auth.idToken,
          this.project.id,
          this.return[index].token,
          index + 1
        )
        .then(response => {
          this.data.items.splice(index, 1, {
            headers: { index: response.data.index },
            url: url,
            rank: rank,
            active: active
          });
          this.scheduleGetIndexCount(url, index, rank, active);
          const isEmptyItems = this.data.items.some(
            item => item.headers.index == null
          );
          if (!isEmptyItems) {
            this.$emit('completeKeywordIndex');
          }
          if (response.data.index) {
            this.isLoading = false;
            // グラフのバーの描画に20msのdelayがあるので少しだけ遅らせる
            this.$nextTick(() => {
              setTimeout(() => {
                this.$emit('loadedKeywordIndex');
              }, 20);
            });
          }
        })
        .catch(error => {
          if (!axios.isCancel(error)) {
            this.scheduleGetIndexCount(url, index, rank, active);
          }
        })
        .finally(() => {});
    },
    scheduleGetIndexCount(url, index, rank, active) {
      if (this.flg) {
        if (this.cnt < 200) {
          this.timeout = setTimeout(() => {
            this.getKeywordIndex(url, index, rank, active);
            this.cnt = this.cnt + 1;
          }, 30000);
        } else {
          this.isLoading = false;
          // グラフのバーの描画に20msのdelayがあるので少しだけ遅らせる
          this.$nextTick(() => {
            setTimeout(() => {
              this.$emit('loadedKeywordIndex');
            }, 20);
          });
          if (this.data.items) {
            for (let i = 0; i < this.data.items.length; i++) {
              if (
                this.data.items[i].headers.index === '' ||
                this.data.items[i].headers.index === null ||
                this.data.items[i].headers.index === undefined
              ) {
                this.isError = true;
              } else {
                this.isError = false;
                break;
              }
            }
          } else {
            this.isError = true;
          }
        }
      }
    },
    reload() {
      location.reload(true);
    }
  }
};
</script>
